import React, { useState } from "react";
import { useData } from "../../../context/DataContext";
import ImageUploadField from "../../../utils/ImageUploadField";
import { confirmDelete, successToast, errorToast } from "../../../utils/dialog";

const AmenitiesAdmin = () => {
  const { amenitiesData, addAmenity, updateAmenity, deleteAmenity } = useData();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    icon: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [iconFile, setIconFile] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIconFile(file);
      const previewUrl = URL.createObjectURL(file);
      setFormData((prev) => ({ ...prev, icon: previewUrl }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingId) {
        await updateAmenity(editingId, formData, iconFile);
        successToast("Amenity updated.");
      } else {
        await addAmenity(formData, iconFile);
        successToast("Amenity added.");
      }
      resetForm();
    } catch (err) {
      console.error(err);
      errorToast("Error saving amenity data.");
    }
  };

  const handleEdit = (amenity) => {
    setFormData(amenity);
    setEditingId(amenity.id);
    setIconFile(null); // Reset the icon file
  };

  const handleDelete = async (id) => {
    if (
      await confirmDelete({
        title: "Delete amenity?",
        text: "This amenity will be permanently removed.",
      })
    ) {
      try {
        await deleteAmenity(id);
        successToast("Amenity deleted.");
      } catch (err) {
        console.error(err);
        errorToast("Error deleting amenity.");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      icon: "",
    });
    setEditingId(null);
    setIconFile(null);
  };

  return (
    <div className="p-6 space-y-8">
      <h2 className="text-3xl font-bold mb-4 text-gray-800">Amenities Admin Panel</h2>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow space-y-4">
        <h3 className="text-2xl font-semibold text-gray-700 mb-4">
          {editingId ? "Edit Amenity" : "Add Amenity"}
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="Amenity title"
            className="w-full p-2 border rounded focus:ring-2 focus:ring-[#D48C1E]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Amenity description"
            rows={3}
            className="w-full p-2 border rounded focus:ring-2 focus:ring-[#D48C1E]"
          />
        </div>
        <ImageUploadField
          label="Icon"
          hint="Recommended size: 100×100px (square)"
          aspect="square"
          previewUrl={formData.icon}
          file={iconFile}
          onChange={handleFileChange}
          onClear={() => {
            setIconFile(null);
            setFormData((prev) => ({ ...prev, icon: "" }));
          }}
        />
        <div className="flex justify-end space-x-4">
          <button
            type="submit"
            className="px-6 py-2 bg-[#14251F] hover:bg-[#1f3a30] text-[#FFD1A0] rounded transition font-medium"
          >
            {editingId ? "Update Amenity" : "Add Amenity"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* Existing Amenities */}
      <div className="bg-white p-6 rounded-lg shadow space-y-4">
        <h3 className="text-2xl font-semibold text-gray-700 mb-4">Existing Amenities</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {amenitiesData.map((amenity) => (
            <div key={amenity.id} className="border rounded p-4 shadow">
              <img
                src={amenity.icon || "https://via.placeholder.com/150"}
                alt={amenity.title}
                className="w-full h-40 object-cover rounded mb-4"
              />
              <h4 className="text-lg font-bold">{amenity.title}</h4>
              <p className="text-sm text-gray-600">{amenity.description}</p>
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  onClick={() => handleEdit(amenity)}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(amenity.id)}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AmenitiesAdmin;